// SettingsWorkspace — the app's settings dialog, shaped like the Claude
// desktop app's: sections down a left sidebar, one scrollable content pane on
// the right, ✕ to close.
//
// This replaces PalettePicker (the modal that ONLY picked palettes) because
// settings kept accruing homes: palettes in one modal, UI sizing nowhere,
// scene backdrop buried in the ?tune=1 LiveTunePanel, sign-out on a bottom
// bar. One dialog, four sections:
//
//   Colors   — the palette picker, unchanged in behavior (setPalette applies
//              live and persists per-user). Keeps the appearance-panel and
//              palette-card-* testids its e2e coverage drives.
//   Display  — font size and icon size (uiScale.js). Font rides a root zoom,
//              icons a CSS var — see that module for why.
//   Scene    — the viewport backdrop: sky color and ground plane. These WRITE
//              THE SAME liveTuneStore.backdrop slice the LiveTunePanel edits —
//              one source of truth, two views; the tune panel stays the
//              power-user surface.
//   Account  — sign out.
//
// Themed by the tokens, so it previews a palette pick live like the old panel.

import React, { useEffect, useState } from 'react'
import FloatingWindow from './FloatingWindow.jsx'

import { palettesByGroup } from '../lib/palettes/registry'
import { getPaletteId, setPalette, subscribe as subscribePalette } from '../lib/palettes/paletteStore'
import { uiScale, FONT_STEPS, ICON_STEPS } from '../lib/uiScale.js'
import { liveTuneStore } from '../lib/liveTuneStore'
import { saveSceneLook, hasSceneLook } from '../lib/saveSceneLook.js'
import { subscribeEventGraph } from '../lib/eventGraphState.js'
import { getActiveCharacterId } from '../lib/characterAuthoringStore'
import { logout as doLogout } from '../lib/auth/logout'
import { useAuth } from '../lib/auth/AuthContext.jsx'

const SECTIONS = [
  { id: 'colors', label: 'Colors' },
  { id: 'display', label: 'Display' },
  { id: 'scene', label: 'Scene' },
  { id: 'account', label: 'Account' },
]

// ── Shared bits ───────────────────────────────────────────────────────────────

function SectionTitle({ children, sub }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>{children}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--m-fg-2)', marginTop: 3, lineHeight: 1.5 }}>{sub}</div>}
    </div>
  )
}

function FieldRow({ label, sub, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0',
      borderBottom: '1px solid var(--m-line)',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: 'var(--m-fg-2)', marginTop: 2, lineHeight: 1.5 }}>{sub}</div>}
      </div>
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>
    </div>
  )
}

function Segmented({ options, value, onPick, testIdPrefix }) {
  return (
    <div style={{ display: 'flex', border: '1px solid var(--m-line-hi)', borderRadius: 6, overflow: 'hidden' }}>
      {options.map(o => {
        const on = o.value === value
        return (
          <button
            key={o.value}
            type="button"
            data-testid={testIdPrefix ? `${testIdPrefix}-${o.value}` : undefined}
            onClick={() => onPick(o.value)}
            style={{
              font: 'inherit', fontSize: 12, padding: '6px 14px', cursor: 'pointer',
              border: 'none',
              borderLeft: o === options[0] ? 'none' : '1px solid var(--m-line-hi)',
              background: on ? 'color-mix(in srgb, var(--m-accent) 18%, transparent)' : 'transparent',
              color: on ? 'var(--m-fg)' : 'var(--m-fg-2)',
            }}
          >{o.label}</button>
        )
      })}
    </div>
  )
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      style={{
        width: 40, height: 22, borderRadius: 999, cursor: 'pointer', position: 'relative',
        border: '1px solid var(--m-line-hi)',
        background: checked ? 'var(--m-accent)' : 'var(--m-panel-hi)',
        transition: 'background .15s',
        padding: 0,
      }}
    >
      <span style={{
        position: 'absolute', top: 2, left: checked ? 20 : 2,
        width: 16, height: 16, borderRadius: '50%',
        background: 'var(--m-fg)', transition: 'left .15s',
      }} />
    </button>
  )
}

function ColorField({ value, onChange, testId }) {
  return (
    <input
      type="color"
      data-testid={testId}
      value={value}
      onChange={e => onChange(e.target.value)}
      style={{
        width: 42, height: 26, padding: 0, border: '1px solid var(--m-line-hi)',
        borderRadius: 4, background: 'transparent', cursor: 'pointer',
      }}
    />
  )
}

// ── Palette bits (from PalettePicker, which this dialog replaces) ─────────────

// A compact swatch strip (bg / panel / accent + the 5 typed wire colors),
// mirroring the design handoff's PaletteStrip. A light palette gets a hairline
// border on its light swatches so they read against the card.
function SwatchStrip({ p }) {
  const sw = (bg, title, bordered = false) => (
    <span
      key={title}
      title={title}
      style={{
        width: 16, height: 16, borderRadius: 3, background: bg, display: 'inline-block',
        boxShadow: bordered ? 'inset 0 0 0 1px rgba(0,0,0,0.25)' : 'inset 0 0 0 1px rgba(255,255,255,0.08)',
      }}
    />
  )
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      {sw(p.bg, 'bg', p.isLight)}
      {sw(p.panel, 'panel', p.isLight)}
      {sw(p.panelHi, 'panelHi', p.isLight)}
      {sw(p.accent, 'accent')}
      <span style={{ width: 6 }} />
      {sw(p.wires.anim, 'anim')}
      {sw(p.wires.audio, 'audio')}
      {sw(p.wires.event, 'event')}
      {sw(p.wires.state, 'state')}
      {sw(p.wires.output, 'output')}
    </div>
  )
}

function PaletteCard({ p, active, onPick }) {
  return (
    <button
      type="button"
      onClick={() => onPick(p.id)}
      data-testid={`palette-card-${p.id}`}
      data-active={active ? '1' : undefined}
      style={{
        textAlign: 'left', cursor: 'pointer', width: 280,
        background: 'var(--m-node-bg)',
        border: `1px solid ${active ? 'var(--m-accent)' : 'var(--m-node-border)'}`,
        outline: active ? '1px solid var(--m-accent)' : 'none',
        borderRadius: 8, padding: 12, color: 'var(--m-fg)',
        display: 'flex', flexDirection: 'column', gap: 8,
        font: 'inherit',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</span>
        {active && <span style={{ fontSize: 11, color: 'var(--m-accent)' }}>✓ active</span>}
      </div>
      <SwatchStrip p={p} />
      <div style={{ fontSize: 11, color: 'var(--m-fg-2)', lineHeight: 1.4 }}>{p.approach}</div>
    </button>
  )
}

// ── Sections ──────────────────────────────────────────────────────────────────

function ColorsSection() {
  const [, tick] = useState(0)
  useEffect(() => subscribePalette(() => tick(t => t + 1)), [])
  const activeId = getPaletteId()
  return (
    <>
      <SectionTitle sub="Pick a color palette. Saved to your account and applied everywhere.">
        Colors
      </SectionTitle>
      {palettesByGroup().map(([group, palettes]) => (
        <div key={group} style={{ marginBottom: 20 }}>
          <div style={{
            fontSize: 10, textTransform: 'uppercase', letterSpacing: 1,
            color: 'var(--m-fg-3)', marginBottom: 10,
          }}>{group}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {palettes.map(p => (
              <PaletteCard key={p.id} p={p} active={p.id === activeId} onPick={setPalette} />
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

const SIZE_OPTIONS = [
  { value: 'small', label: 'Small' },
  { value: 'medium', label: 'Medium' },
  { value: 'large', label: 'Large' },
]

function DisplaySection() {
  const [scale, setScale] = useState(uiScale.getState())
  useEffect(() => uiScale.subscribe(setScale), [])
  return (
    <>
      <SectionTitle sub="Sizing applies immediately and is saved on this browser.">
        Display
      </SectionTitle>
      <FieldRow
        label="Font size"
        sub={`Scales all interface text (${Math.round(FONT_STEPS[scale.font] * 100)}%).`}
      >
        <Segmented options={SIZE_OPTIONS} value={scale.font} onPick={v => uiScale.setState({ font: v })} testIdPrefix="settings-font" />
      </FieldRow>
      <FieldRow
        label="Icon size"
        sub={`Scales the tool rail and menu glyphs (${Math.round(ICON_STEPS[scale.icon] * 100)}%).`}
      >
        <Segmented options={SIZE_OPTIONS} value={scale.icon} onPick={v => uiScale.setState({ icon: v })} testIdPrefix="settings-icon" />
      </FieldRow>
    </>
  )
}

function SceneSection() {
  const [backdrop, setBackdrop] = useState(liveTuneStore.getState().backdrop)
  useEffect(() => liveTuneStore.subscribe(() => setBackdrop({ ...liveTuneStore.getState().backdrop })), [])
  const set = patch => liveTuneStore.setState({ backdrop: patch })

  // "Save look to character" — the answer to "no way to save the settings for BG
  // color or Camera without tying it to a heavy GSplat" (Isaac, 2026-09-01).
  // Everything above writes liveTuneStore, which persists per BROWSER; this
  // writes the same values into the CHARACTER's event blueprint, so they travel
  // with it and reach a teammate. See sceneLookNodes.js for why that is the
  // store and not a new column.
  const charId = getActiveCharacterId()
  const [look, setLook] = useState({ busy: false, msg: null, ok: true })
  const [saved, setSaved] = useState(() => hasSceneLook())
  useEffect(() => subscribeEventGraph(() => setSaved(hasSceneLook())), [])

  const run = async (tune) => {
    setLook({ busy: true, msg: null, ok: true })
    const r = await saveSceneLook(charId, tune)
    setLook({ busy: false, msg: r.message, ok: r.ok })
    setSaved(hasSceneLook())
  }

  return (
    <>
      <SectionTitle sub="The viewport backdrop — same controls as the tune panel's BACKDROP section, one source of truth.">
        Scene
      </SectionTitle>
      <FieldRow label="Background" sub="Fill the viewport sky with a solid color (part of the rendered frame, so it shows in recordings).">
        <ColorField value={backdrop.skyColor} onChange={v => set({ skyEnabled: true, skyColor: v })} testId="settings-sky-color" />
        <Toggle checked={backdrop.skyEnabled} onChange={v => set({ skyEnabled: v })} testId="settings-sky-enabled" />
      </FieldRow>
      <FieldRow label="Ground plane" sub="An opaque floor at the stage height, so the character doesn't read as floating.">
        <ColorField value={backdrop.groundColor} onChange={v => set({ groundEnabled: true, groundColor: v })} testId="settings-ground-color" />
        <Toggle checked={backdrop.groundEnabled} onChange={v => set({ groundEnabled: v })} testId="settings-ground-enabled" />
      </FieldRow>
      <FieldRow label="Hide environment" sub="Hide the environment scan so the backdrop is what you see. Lighting keeps using it.">
        <Toggle checked={backdrop.hideEnvironment} onChange={v => set({ hideEnvironment: v })} testId="settings-hide-env" />
      </FieldRow>

      <FieldRow
        label="Save look to this character"
        sub={charId
          ? 'Stores the background AND the current camera on the character itself, so it loads this way for everyone — no environment needed. Everything above is otherwise only saved in this browser.'
          : 'No character loaded — open one first.'}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Shown-and-disabled, never hidden: a save button that disappears
              reads as a missing feature rather than an unmet precondition. */}
          <button
            type="button"
            data-testid="settings-save-look"
            disabled={!charId || look.busy}
            onClick={() => run(liveTuneStore.getState())}
            style={{
              padding: '4px 10px', fontSize: 12, borderRadius: 4, cursor: charId ? 'pointer' : 'not-allowed',
              border: '1px solid var(--m-line)', background: 'var(--m-bg-2)', color: 'var(--m-fg-1)',
              opacity: charId ? 1 : 0.5,
            }}
          >
            {look.busy ? 'saving…' : 'Save'}
          </button>
          <button
            type="button"
            data-testid="settings-clear-look"
            disabled={!charId || look.busy || !saved}
            onClick={() => run(null)}
            title={saved ? 'Remove the saved look — the character goes back to defaults' : 'This character has no saved look'}
            style={{
              padding: '4px 10px', fontSize: 12, borderRadius: 4,
              cursor: charId && saved ? 'pointer' : 'not-allowed',
              border: '1px solid var(--m-line)', background: 'none', color: 'var(--m-fg-2)',
              opacity: charId && saved ? 1 : 0.5,
            }}
          >
            Clear
          </button>
        </div>
      </FieldRow>
      {look.msg && (
        <div
          data-testid="settings-look-status"
          style={{
            fontSize: 11, marginTop: -4, marginBottom: 8, paddingLeft: 2,
            color: look.ok ? 'var(--m-online)' : 'var(--m-danger)',
          }}
        >
          {look.msg}
        </div>
      )}
    </>
  )
}

function AccountSection() {
  const { me } = useAuth()
  const [busy, setBusy] = useState(false)
  return (
    <>
      <SectionTitle sub="Session and account actions.">Account</SectionTitle>
      <FieldRow label="Signed in" sub={me?.email ?? '—'}>
        <button
          type="button"
          data-testid="settings-sign-out"
          disabled={busy}
          onClick={() => {
            if (busy) return
            setBusy(true)
            // logout() redirects to the portal /logout — the browser navigates
            // away, so there is nothing to do after.
            doLogout()
          }}
          style={{
            font: 'inherit', fontSize: 12, padding: '7px 16px', cursor: busy ? 'default' : 'pointer',
            borderRadius: 6,
            border: '1px solid color-mix(in srgb, var(--m-danger) 55%, transparent)',
            background: 'color-mix(in srgb, var(--m-danger) 12%, transparent)',
            color: busy ? 'var(--m-fg-3)' : 'var(--m-danger)',
          }}
        >{busy ? 'Signing out…' : 'Sign out'}</button>
      </FieldRow>
    </>
  )
}

// ── The dialog ────────────────────────────────────────────────────────────────

export default function SettingsWorkspace({ open, onClose, initialSection = 'colors' }) {
  const [section, setSection] = useState(initialSection)

  // Close on Escape for keyboard parity with the app's other overlays.
  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') onClose?.() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Reopening lands on the section the caller asked for.
  useEffect(() => { if (open) setSection(initialSection) }, [open, initialSection])

  if (!open) return null

  // A movable window, not a modal (Rebecca, 2026-08-06): there is no backdrop,
  // so the viewport stays visible AND interactive while Settings is open — a
  // Scene background/plane change is seen the moment it is made, and the whole
  // window drags anywhere / pops out to a second monitor (FloatingWindow).
  return (
    <FloatingWindow
      id="settings"
      title="SETTINGS"
      onClose={onClose}
      closeTestId="appearance-close"
      dockable={false}
      defaultSize={{ w: 1000, h: 660 }}
    >
      <div
        data-testid="appearance-panel"
        style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}
      >
      <div
        data-testid="settings-workspace"
        style={{
          flex: 1, minWidth: 0, display: 'flex',
          background: 'var(--m-panel)', color: 'var(--m-fg)',
          overflow: 'hidden',
        }}
      >
        {/* Sidebar */}
        <div style={{
          flex: 'none', width: 200, background: 'var(--m-panel-hi)',
          borderRight: '1px solid var(--m-line)', padding: '18px 10px',
          display: 'flex', flexDirection: 'column', gap: 2,
        }}>
          <div style={{
            fontSize: 10, textTransform: 'uppercase', letterSpacing: 1,
            color: 'var(--m-fg-3)', padding: '0 10px 8px',
          }}>Settings</div>
          {SECTIONS.map(sec => {
            const on = sec.id === section
            return (
              <button
                key={sec.id}
                type="button"
                data-testid={`settings-section-${sec.id}`}
                onClick={() => setSection(sec.id)}
                style={{
                  font: 'inherit', fontSize: 13, textAlign: 'left', cursor: 'pointer',
                  padding: '8px 10px', borderRadius: 6, border: 'none',
                  background: on ? 'color-mix(in srgb, var(--m-accent) 16%, transparent)' : 'transparent',
                  color: on ? 'var(--m-fg)' : 'var(--m-fg-2)',
                  fontWeight: on ? 600 : 400,
                }}
              >{sec.label}</button>
            )
          })}
        </div>

        {/* Content — the window bar (FloatingWindow) owns the close ✕ now;
            a second in-content close row would be the double-✕ this removed. */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div className="aos-thin-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 28px 28px' }}>
            {section === 'colors' && <ColorsSection />}
            {section === 'display' && <DisplaySection />}
            {section === 'scene' && <SceneSection />}
            {section === 'account' && <AccountSection />}
          </div>
        </div>
      </div>
      </div>
    </FloatingWindow>
  )
}
